import React, { useEffect, useState } from 'react';
import {
  Device,
  EnergyController,
  useEnergyControllerDetailQuery,
  useEnergyControllerDetailUpdatesSubscription,
} from '../../../../types/generated-types';
import DeviceLoadingSkeleton from '../shared-ui/deviceLoadingSkeleton';
import { formatDeviceMetric } from '../../../utils/formatters';
import { DeviceInformation } from '../shared-ui/device-info';
import { PanelFrame, ViewMode } from '../shared-ui/panel-frame';
import { Box, Grid, Typography } from '@mui/material';
import moment from 'moment';
import { updateCacheFromSubscriptionEvent } from '../../../../helpers/subscriptionUtils';
import Divider from '@mui/material/Divider';
import { EnergyRectifierComponent } from './energy-rectifier';
import Stack from '@mui/material/Stack';

interface EnergyControllerProps {
  id: string;
}

export const EnergyControllerComponent: React.FC<EnergyControllerProps> = ({
  id,
}) => {
  const [device, setDevice] = useState<Partial<EnergyController>>();
  const [deviceInfo, setDeviceInfo] = useState<DeviceInformation[]>([]);

  const { data } = useEnergyControllerDetailQuery({
    variables: { id },
  });

  useEnergyControllerDetailUpdatesSubscription({
    variables: { id },
    fetchPolicy: 'no-cache',
    onData: updateCacheFromSubscriptionEvent,
  });

  useEffect(() => {
    if (data?.energyControllerById) {
      setDevice(data.energyControllerById as Partial<EnergyController>);
    }
  }, [data]);

  useEffect(() => {
    if (device) {
      const info: DeviceInformation[] = [
        {
          'Device Identification': {
            'Device Name': device.name ?? 'Unnamed Energy Controller',
            'Device ID': device.deviceId ?? 'Unidentified',
            'Device Type': device.typeDisplayName ?? 'Unknown',
            'Last Updated': moment(device.timestamp).fromNow(),
          },
          'Power Generation': {
            'Voltage at MPP': formatDeviceMetric({
              value: device.voltageAtMpp?.value ?? undefined,
              units: device.voltageAtMpp?.units ?? undefined,
            }),
            'Current at MPP': formatDeviceMetric({
              value: device.currentAtMpp?.value ?? undefined,
              units: device.currentAtMpp?.units ?? undefined,
            }),
            ...(device.rotorSpeedAtMPP?.value !== undefined
              ? {
                  'Rotor Speed at MPP': formatDeviceMetric({
                    value: device.rotorSpeedAtMPP.value,
                    units: device.rotorSpeedAtMPP.units,
                  }),
                }
              : {}),
          },
          'System Status': {
            'Power State': device.powerState?.value ? 'On' : 'Off',
            'Is Powered On': device.isPoweredOn ? 'Yes' : 'No',
            'Availability Status':
              device.availabilityStatus?.value ?? 'Unknown',
            'Charge Regulation':
              device.chargeRegulationStatus?.value ?? 'Unknown',
          },
          Configuration: {
            'Is Configured': device.isConfigured ? 'Yes' : 'No',
            'Is Controlled': device.isControlled ? 'Yes' : 'No',
            'Has Pending Updates': device.hasPendingUpdates ? 'Yes' : 'No',
          },
          'System Integration': {
            Property: device.property?.title ?? 'Not assigned',
            Unit: device.unit?.name ?? 'Not assigned',
            Panel: device.panel?.displayName ?? 'Not assigned',
            Source: device.source?.name ?? 'Not assigned',
          },
        },
      ];
      setDeviceInfo(info);
    }
  }, [device]);

  if (!device) {
    return <DeviceLoadingSkeleton size="large" />;
  }

  // Calculate current power (P = V × I)
  const currentPower =
    device.voltageAtMpp?.value && device.currentAtMpp?.value
      ? device.voltageAtMpp.value * device.currentAtMpp.value
      : undefined;

  return (
    <PanelFrame
      title="Energy Controller"
      iconType="mdi:transmission-tower-export"
      device={device as Partial<Device>}
      infoEntries={deviceInfo}
      viewMode={ViewMode.NORMAL}
      editingEnabled={false}
    >
      <Box sx={{ p: 2 }}>
        <Stack
          direction={'row'}
          justifyContent={'space-between'}
          divider={<Divider orientation="vertical" flexItem />}
        >
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={4}>
              <Typography
                variant="subtitle2"
                color="textSecondary"
                align="center"
              >
                Energy Generated
              </Typography>
              <Typography variant="h6" align="center">
                {formatDeviceMetric({
                  value: device.energyGenerated?.value ?? undefined,
                  units: device.energyGenerated?.units ?? undefined,
                })}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Typography
                variant="subtitle2"
                color="textSecondary"
                align="center"
              >
                Current Power
              </Typography>
              <Typography variant="h6" align="center">
                {currentPower !== undefined
                  ? `${currentPower.toFixed(2)} W`
                  : 'Unknown'}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Typography
                variant="subtitle2"
                color="textSecondary"
                align="center"
              >
                Availability Status
              </Typography>
              <Typography variant="h6" align="center">
                {device.availabilityStatus?.value ?? 'Unknown'}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Typography
                variant="subtitle2"
                color="textSecondary"
                align="center"
              >
                Charge Regulation
              </Typography>
              <Typography variant="h6" align="center">
                {device.chargeRegulationStatus?.value ?? 'Unknown'}
              </Typography>
            </Grid>
          </Grid>
          {device.rectifierId ? (
            <EnergyRectifierComponent id={device.rectifierId} />
          ) : null}
        </Stack>
      </Box>
    </PanelFrame>
  );
};
